import { analytics, FEATURES_EVENTS } from '@/common/logs';
import * as env from '@/common/environment';
import { getRandomInt } from './core.utils';

interface CommonABTestingParams {
  userId?: string | number;
  enableAbTesting?: boolean | null;
  abValue?: string | number | null;
  lsKey: string;
  requireUserId?: boolean;
  defaultUserId?: string | number;
  bypassConfig?: {
    bypassAbTestingUserIds?: string[];
  };
  defaultResult?: {
    noUserId?: boolean;
    disabled?: boolean;
    noValue?: boolean;
  };
  onEligible?: () => void;
}

const checkBypassAbTesting = (userId?: string | number) => {
  const bypassConfig = env.get('AI') || { bypassAbTestingUserIds: [] };

  if (bypassConfig?.bypassAbTestingUserIds && userId) {
    const bypassAbTesting = bypassConfig.bypassAbTestingUserIds.includes(String(userId));
    if (bypassAbTesting) {
      return true;
    }
  }
}

const executeCommonABTesting = (params: CommonABTestingParams): boolean => {
  const {
    userId,
    enableAbTesting,
    abValue,
    lsKey,
    defaultResult = {},
    onEligible
  } = params;

  if (!userId) {
    return defaultResult.noUserId ?? false;
  }

  // Check bypass configuration
  if (checkBypassAbTesting(userId)) {
    return true;
  }

  // Apply validation rules
  if (!enableAbTesting) {
    return defaultResult.disabled ?? false;
  }

  if (!abValue) {
    return defaultResult.noValue ?? false;
  }

  const numberAbValue = Number(abValue);
  const valueSavedInLS = localStorage.getItem(lsKey);

  const parsedAbValue: {
    configAbValue: string;
    userAbValue: string;
  } | null = valueSavedInLS ? JSON.parse(valueSavedInLS) : null;

  if (abValue && parsedAbValue && Number(parsedAbValue.configAbValue) === numberAbValue) {
    return Number(parsedAbValue.userAbValue) <= numberAbValue;
  } else {
    const userAbValue = getRandomInt(1, 100);

    localStorage.setItem(
      lsKey,
      JSON.stringify({ configAbValue: abValue, userAbValue })
    );

    const isEligible = userAbValue <= numberAbValue;

    if (isEligible && onEligible) {
      onEligible();
    }

    return isEligible;
  }
};

export const checkABTestingPassed = (params: {
  userId?: string | number;
  enableAbTesting: boolean;
  abValue?: string | number;
  articleId: string | number;
}) => {
  const { userId, enableAbTesting, abValue, articleId } = params;

  return executeCommonABTesting({
    userId,
    enableAbTesting,
    abValue,
    lsKey: `abValue-${articleId}-${userId}`,
    defaultResult: {
      noUserId: true,
      disabled: true,
      noValue: true
    }
  });
};

export const checkAiABTestingPassed = (params: {
  userId?: string | number;
  enableAbTesting?: boolean | null;
  abValue?: string | number | null;
}) => {
  const { enableAbTesting, abValue } = params;

  const userId = params.userId || analytics.sessionId;

  return executeCommonABTesting({
    userId,
    enableAbTesting,
    abValue,
    lsKey: `AI-abValue-${userId}`,
    defaultResult: {
      disabled: true,
      noValue: false
    },
    onEligible: () => {
      analytics.trackAiEvent({
        name: FEATURES_EVENTS.AI_ELIGIBLE,
      });
    }
  });
};

export const checkAiTransactionABTestingPassed = (params: {
  userId?: string | number;
  enableAbTesting?: boolean | null;
  abValue?: string | number | null;
  aiAbTestPassed?: boolean;
}) => {
  const { enableAbTesting, abValue, aiAbTestPassed } = params;
  const userId = params.userId || analytics.sessionId;

  if (!aiAbTestPassed) return false;

  return executeCommonABTesting({
    userId,
    enableAbTesting,
    abValue,
    lsKey: `AI-transaction-abValue-${userId}`,
    requireUserId: true,
    defaultResult: {
      disabled: false,
      noValue: false
    },
    onEligible: () => {
      analytics.trackAiEvent({
        name: FEATURES_EVENTS.AI_TRANSACTION_ELIGIBLE,
      });
    }
  });
};

export const checkAiGuestABTestingPassed = (params: {
  sessionId?: string | number;
  enableAbTesting?: boolean | null;
  abValue?: string | number | null;
}) => {
  const { sessionId = analytics.sessionId, enableAbTesting, abValue } = params;

  return executeCommonABTesting({
    userId: sessionId,
    enableAbTesting,
    abValue,
    lsKey: `AI-guest-abValue-${sessionId}`,
    defaultResult: {
      disabled: false,
      noValue: false
    }
  });
};

export const checkAiBetABTestingPassed = (params: {
  userId?: string | number;
  enableAbTesting?: boolean | null;
  abValue?: string | number | null;
  aiAbTestPassed?: boolean;
}) => {
  const { enableAbTesting, abValue, aiAbTestPassed } = params;
  const userId = params.userId || analytics.sessionId;

  if (!aiAbTestPassed) return false;

  return executeCommonABTesting({
    userId,
    enableAbTesting,
    abValue,
    lsKey: `AI-bets-abValue-${userId}`,
    requireUserId: true,
    defaultResult: {
      disabled: false,
      noValue: false
    },
    onEligible: () => {
      analytics.trackAiEvent({
        name: FEATURES_EVENTS.AI_BETS_ELIGIBLE,
      });
    }
  });
};